import React from "react";
//#region types
import { TimelineStepProps } from "models/propTypes/chartProps";
import { ChartEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Icon, InfoTooltip, StaticInfoMessage } from "components/common";
import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Grid } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const TimelineStep: React.FC<TimelineStepProps> = ({ item, isLastStep, nextStepType }) => {
  const iconMapping = {
    [ChartEnums.TimelineStepType.InProgress]: IconDefinitionConstants.WClock2,
    [ChartEnums.TimelineStepType.Blocked]: IconDefinitionConstants.WClock2,
    [ChartEnums.TimelineStepType.Delayed]: IconDefinitionConstants.WClock2,
    [ChartEnums.TimelineStepType.Done]: IconDefinitionConstants.WCheckCanvas,
    [ChartEnums.TimelineStepType.NotStarted]: IconDefinitionConstants.WEmpty,
  };

  const icon = iconMapping[item.type];

  const getStepClass = (itemType: ChartEnums.TimelineStepType) => {
    switch (itemType) {
      case ChartEnums.TimelineStepType.InProgress:
      case ChartEnums.TimelineStepType.Blocked:
        return cssExports.started;
      case ChartEnums.TimelineStepType.Done:
        return cssExports.done;
      case ChartEnums.TimelineStepType.Delayed:
        return cssExports.delayed;
      default:
        return cssExports.notStarted;
    }
  };

  const isTooltipAsStaticMessage = item.type === ChartEnums.TimelineStepType.Done && isLastStep;

  return (
    <>
      <TimelineItem className={clsx(cssExports.timelineItem, getStepClass(item.type))}>
        <TimelineSeparator>
          <TimelineDot className={cssExports.timelineDot}>
            {item.type !== ChartEnums.TimelineStepType.NotStarted ? <Icon icon={icon} /> : <></>}
          </TimelineDot>
          {isLastStep ? (
            <></>
          ) : (
            <TimelineConnector
              className={clsx(
                cssExports.timelineConnector,
                nextStepType !== undefined ? getStepClass(nextStepType) : ""
              )}
            />
          )}
        </TimelineSeparator>
        <TimelineContent className={cssExports.timelineStepContent}>
          <Grid item container direction="row" columnGap={1}>
            <Grid item> {item.text}</Grid>
            {item.subText ? (
              Array.isArray(item.subText) ? (
                item.subText.map((text, index) => (
                  <React.Fragment key={index}>
                    <Grid item className={cssExports.subText}>
                      {text}
                    </Grid>
                    <Grid item className={clsx(cssExports.subText, cssExports.seperator)}>
                      |
                    </Grid>
                  </React.Fragment>
                ))
              ) : (
                <Grid item className={cssExports.subText}>
                  {item.subText}
                </Grid>
              )
            ) : (
              <></>
            )}
            {item.toolTip && !isTooltipAsStaticMessage ? (
              <Grid item>
                <InfoTooltip icon={IconDefinitionConstants.WInfo} text={item.toolTip} placement="right" />
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
          {item.subItems?.length ? (
            <Grid container direction="column" className={cssExports.timelineSubItems}>
              {item.subItems.map((subItem, index) => (
                <Grid
                  key={index}
                  item
                  container
                  direction="row"
                  columnGap={1}
                  alignItems="center"
                  className={clsx(cssExports.timelineSubItem, getStepClass(subItem.type))}
                >
                  <Grid item className={cssExports.timelineSubItemIcon}>
                    <Icon icon={iconMapping[subItem.type]} />
                  </Grid>
                  <Grid item>{subItem.text}</Grid>
                  {subItem.subText ? (
                    <Grid item className={cssExports.subText}>
                      {subItem.subText}
                    </Grid>
                  ) : (
                    <></>
                  )}
                </Grid>
              ))}
            </Grid>
          ) : (
            <></>
          )}
        </TimelineContent>
      </TimelineItem>
      {item.toolTip && isTooltipAsStaticMessage ? (
        <Grid item container marginTop={2}>
          <StaticInfoMessage gutterBottom>{item.toolTip}</StaticInfoMessage>
        </Grid>
      ) : (
        <></>
      )}
    </>
  );
};

export default TimelineStep;
